import React, { useState } from 'react';
import { Card, Table, Button, Switch, Tag, Modal, Form, Input, Select, Space, Tooltip, message } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const AutomationConfigPanel = ({ companyId, configs, onConfigChange, isLoading }) => {
  const { t } = useTranslation(['tables', 'forms', 'messages']);
  const pa = (key) => t(`payrollAutomation.${key}`, { ns: 'tables' });

  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [form] = Form.useForm();
  const [showCustomCron, setShowCustomCron] = useState(false);

  const RULE_TYPES = [
    { value: 'AUTO_CALCULATE_OVERTIME', label: pa('autoCalculateOvertime') },
    { value: 'AUTO_SUBMIT_TIMESHEET', label: pa('autoSubmitTimesheet') },
    { value: 'AUTO_GENERATE_SALARY', label: pa('autoGenerateSalary') },
    { value: 'AUTO_SEND_REMINDERS', label: pa('autoSendReminders') },
    { value: 'AUTO_VALIDATE_TIMESHEETS', label: pa('autoValidateTimesheets') },
  ];

  const TRIGGER_TYPES = [
    { value: 'SCHEDULE', label: pa('scheduleCron') },
    { value: 'EVENT', label: pa('eventTrigger') },
    { value: 'MANUAL', label: pa('manualOnly') },
  ];

  const SCHEDULE_PRESETS = [
    { value: '0 9 * * *', label: pa('dailyAt9') },
    { value: '0 18 * * *', label: pa('dailyAt6pm') },
    { value: '0 9 * * 1', label: pa('everyMonday9') },
    { value: '0 9 1 * *', label: pa('monthlyFirst9') },
    { value: '0 9 25 * *', label: pa('monthly25th9') },
    { value: 'custom', label: pa('customCron') },
  ];

  const handleAddConfig = () => {
    setEditingConfig(null);
    form.resetFields();
    setShowCustomCron(false);
    setModalVisible(true);
  };

  const handleEditConfig = (config) => {
    setEditingConfig(config);

    // Check if schedule matches a preset
    const matchingPreset = SCHEDULE_PRESETS.find(p => p.value === config.schedule);
    const scheduleValue = matchingPreset ? config.schedule : 'custom';
    setShowCustomCron(scheduleValue === 'custom');

    form.setFieldsValue({
      ...config,
      schedulePreset: scheduleValue,
      customSchedule: scheduleValue === 'custom' ? config.schedule : undefined,
    });
    setModalVisible(true);
  };

  const handleDeleteConfig = async (configId) => {
    Modal.confirm({
      title: pa('deleteAutomationRule'),
      content: pa('deleteAutomationRuleDesc'),
      okText: pa('delete'),
      okType: 'danger',
      cancelText: pa('cancel'),
      onOk: async () => {
        try {
          await onConfigChange('delete', configId);
          message.success(pa('automationRuleDeleted'));
        } catch {
          message.error(t('operationError', { ns: 'messages' }));
        }
      },
    });
  };

  const handleToggle = async (configId, enabled) => {
    try {
      await onConfigChange('toggle', configId, { enabled });
      message.success(pa('automationToggleSuccess'));
    } catch {
      message.error(t('operationError', { ns: 'messages' }));
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Handle schedule
      let schedule = values.schedulePreset;
      if (values.schedulePreset === 'custom') {
        schedule = values.customSchedule;
      }

      const configData = {
        company: companyId,
        ruleType: values.ruleType,
        triggerType: values.triggerType,
        schedule: values.triggerType === 'SCHEDULE' ? schedule : undefined,
        enabled: values.enabled !== undefined ? values.enabled : true,
        config: values.config || {},
        description: values.description,
      };

      if (editingConfig) {
        await onConfigChange('update', editingConfig._id, configData);
        message.success(pa('automationRuleUpdated'));
      } else {
        await onConfigChange('create', null, configData);
        message.success(pa('automationRuleCreated'));
      }

      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleExecuteNow = async (configId, ruleType) => {
    Modal.confirm({
      title: pa('executeNow'),
      content: pa('executeNowDesc'),
      okText: pa('execute'),
      cancelText: pa('cancel'),
      onOk: async () => {
        try {
          await onConfigChange('execute', configId, { ruleType, companyId });
          message.success(pa('automationExecutionStarted'));
        } catch {
          message.error(t('operationError', { ns: 'messages' }));
        }
      },
    });
  };

  const columns = [
    {
      title: pa('ruleType'),
      dataIndex: 'ruleType',
      key: 'ruleType',
      render: (ruleType) => {
        const rule = RULE_TYPES.find(r => r.value === ruleType);
        return (
          <div>
            <SettingOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            {rule?.label || ruleType}
          </div>
        );
      },
    },
    {
      title: pa('trigger'),
      dataIndex: 'triggerType',
      key: 'triggerType',
      render: (triggerType, record) => {
        const trigger = TRIGGER_TYPES.find(t => t.value === triggerType);
        return (
          <div>
            <div>{trigger?.label || triggerType}</div>
            {triggerType === 'SCHEDULE' && record.schedule && (
              <Tag color="blue" style={{ marginTop: 4 }}>
                {record.schedule}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: pa('descriptionLabel'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description) => (
        <Tooltip title={description}>
          {description || '-'}
        </Tooltip>
      ),
    },
    {
      title: pa('lastRun'),
      dataIndex: 'lastExecutionAt',
      key: 'lastExecutionAt',
      render: (lastExecutionAt) => {
        if (!lastExecutionAt) return <Tag>{pa('never')}</Tag>;
        return (
          <Tooltip title={dayjs(lastExecutionAt).format('YYYY-MM-DD HH:mm:ss')}>
            {dayjs(lastExecutionAt).fromNow()}
          </Tooltip>
        );
      },
    },
    {
      title: t('status', { ns: 'tables' }),
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled, record) => (
        <Switch
          checked={enabled}
          onChange={(checked) => handleToggle(record._id, checked)}
          checkedChildren={pa('on')}
          unCheckedChildren={pa('off')}
        />
      ),
    },
    {
      title: t('actions', { ns: 'tables' }),
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={pa('executeNow')}>
            <Button
              type="primary"
              size="small"
              className="btn-primary"
              icon={<ThunderboltOutlined />}
              onClick={() => handleExecuteNow(record._id, record.ruleType)}
            >
              {pa('run')}
            </Button>
          </Tooltip>
          <Button
            type="default"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditConfig(record)}
          />
          <Button
            type="default"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteConfig(record._id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={pa('automationRulesConfiguration')}
        extra={
          <Button type="primary" className="btn-primary" icon={<PlusOutlined />} onClick={handleAddConfig}>
            {pa('addRule')}
          </Button>
        }
        style={{ marginBottom: 24 }}
        loading={isLoading}
      >
        <Table
          dataSource={configs}
          columns={columns}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>

      {/* Config Modal */}
      <Modal
        title={editingConfig ? pa('editAutomationRule') : pa('addAutomationRule')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => { setModalVisible(false); form.resetFields(); }}>
              {pa('cancel')}
            </button>
            <button className="btn-primary" onClick={handleSubmit}>
              {editingConfig ? pa('update') : pa('create')}
            </button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="ruleType"
            label={pa('ruleType')}
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
          >
            <Select placeholder={pa('selectRuleType')}>
              {RULE_TYPES.map(rule => (
                <Option key={rule.value} value={rule.value}>
                  {rule.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="triggerType"
            label={pa('triggerType')}
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
          >
            <Select
              placeholder={pa('selectTriggerType')}
              onChange={(value) => {
                if (value !== 'SCHEDULE') {
                  form.setFieldsValue({ schedulePreset: undefined, customSchedule: undefined });
                }
              }}
            >
              {TRIGGER_TYPES.map(trigger => (
                <Option key={trigger.value} value={trigger.value}>
                  {trigger.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.triggerType !== currentValues.triggerType
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('triggerType') === 'SCHEDULE' ? (
                <>
                  <Form.Item
                    name="schedulePreset"
                    label={pa('schedulePreset')}
                    rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
                  >
                    <Select
                      placeholder={pa('selectSchedulePreset')}
                      onChange={(value) => setShowCustomCron(value === 'custom')}
                    >
                      {SCHEDULE_PRESETS.map(preset => (
                        <Option key={preset.value} value={preset.value}>
                          {preset.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {showCustomCron && (
                    <Form.Item
                      name="customSchedule"
                      label={pa('cronExpression')}
                      rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
                      help={pa('cronExpressionHelp')}
                    >
                      <Input placeholder="0 9 * * *" />
                    </Form.Item>
                  )}
                </>
              ) : null
            }
          </Form.Item>

          <Form.Item name="description" label={pa('descriptionLabel')}>
            <TextArea rows={3} placeholder={pa('descriptionPlaceholder')} />
          </Form.Item>

          <Form.Item name="enabled" label={pa('enabled')} valuePropName="checked" initialValue={true}>
            <Switch checkedChildren={pa('yes')} unCheckedChildren={pa('no')} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AutomationConfigPanel;
